import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function HowItWorks() {
  return (
    <>
      <Helmet>
        <title>How It Works - RMQ Digital Platform</title>
        <meta name="description" content="Learn how RMQ Digital's self-service communication platform works. Businesses manage their own customer notifications and alerts." />
      </Helmet>

      <div style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)', color: 'white', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}>How It Works</h1>
          <p style={{ fontSize: '1.25rem', opacity: 0.95 }}>Self-service platform for business communications</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        {/* Platform Overview Image */}
        <div style={{ marginBottom: '4rem', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <img 
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=400&fit=crop" 
            alt="Team working on platform" 
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
          />
        </div>

        <div style={{ background: '#fef3c7', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', border: '2px solid #f59e0b' }}>
          <h2 style={{ color: '#92400e', marginBottom: '1rem', fontSize: '1.5rem' }}>RMQ Digital Platform Model</h2>
          <p style={{ color: '#78350f', fontSize: '1.1rem', lineHeight: '1.8' }}>
            RMQ Digital provides a <strong>self-service software platform</strong> where verified businesses can manage their own customer communications. 
            Each business maintains their own customer lists, collects their own opt-ins, and controls their own messaging content.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>1️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Business Registration
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              Businesses sign up for an account and complete verification. We verify business legitimacy, EIN, and contact information before activation.
            </p>
          </div>

          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>2️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Customer Opt-In Collection
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              Each business collects opt-in consent from THEIR OWN customers through their website, app, or in-person signup. We provide opt-in form templates.
            </p>
          </div>

          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>3️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Self-Service Dashboard
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              Businesses log into their own dashboard to upload contacts, create messages, and schedule notifications. They control all content and timing.
            </p>
          </div>

          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>4️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Compliance Monitoring
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              We monitor all messaging for compliance. Businesses must maintain opt-in records and honor STOP requests. Non-compliant accounts are suspended.
            </p>
          </div>
        </div>

        <div style={{ background: '#f9fafb', padding: '3rem', borderRadius: '12px', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem', textAlign: 'center' }}>
            What Businesses Can Do
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {[
              'Send appointment reminders to their customers',
              'Send order status updates',
              'Send account notifications',
              'Send service alerts',
              'Send payment reminders',
              'Send delivery notifications',
              'Send customer support messages',
              'Send verification codes (OTP)',
              'Manage their own contact lists',
              'View delivery reports and analytics'
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#6d28d9', fontWeight: '600' }}>✓</span>
                <span style={{ color: '#4b5563' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: '#fee2e2', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', border: '2px solid #ef4444' }}>
          <h2 style={{ color: '#991b1b', marginBottom: '1rem', fontSize: '1.5rem' }}>What We Do NOT Allow</h2>
          <ul style={{ color: '#7f1d1d', fontSize: '1.05rem', lineHeight: '2', paddingLeft: '2rem' }}>
            <li>Sending messages on behalf of businesses (we provide the tools, they send)</li>
            <li>Purchased or scraped contact lists</li>
            <li>Messages without documented opt-in consent</li>
            <li>Unsolicited promotional messages</li>
            <li>High-risk content (loans, crypto, gambling, etc.)</li>
            <li>Sharing customer data between businesses</li>
          </ul>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)', color: 'white', padding: '3rem', borderRadius: '12px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
            Ready to Use Our Platform?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.95 }}>
            Sign up for a verified business account today
          </p>
          <Link to="/contact" style={{
            display: 'inline-block',
            background: 'white',
            color: '#6d28d9',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem'
          }}>
            Get Started
          </Link>
        </div>
      </div>
    </>
  );
}
